import { useEffect, useState } from "react";

// Full-screen splash for the PWA. Shows once per browser session
// (sessionStorage) to avoid flashing on every route change while hot
// reloading. Mimics the iOS native splash: logo centered, subtle scale-in,
// tagline fade-in, then a 300ms fade-out.
const SESSION_KEY = "cb_splash_shown_v1";
const VISIBLE_MS = 1400;
const FADE_MS = 450;

function alreadyShown() {
  if (typeof window === "undefined") return true;
  return sessionStorage.getItem(SESSION_KEY) === "1";
}

export default function Splash() {
  const [phase, setPhase] = useState(() => (alreadyShown() ? "hidden" : "entering"));

  // Schedule timers exactly once on mount.
  useEffect(() => {
    if (alreadyShown()) return undefined;
    const t1 = setTimeout(() => setPhase("leaving"), VISIBLE_MS);
    const t2 = setTimeout(() => {
      try { sessionStorage.setItem(SESSION_KEY, "1"); } catch (e) {
      // Safari private mode or storage quota — splash will appear again
      // next time, which is harmless. Log only in dev.
      if (typeof process !== "undefined" && process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.debug("Splash session flag could not be persisted:", e);
      }
    }
      setPhase("hidden");
    }, VISIBLE_MS + FADE_MS);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (phase === "hidden") return null;

  return (
    <div
      data-testid="splash-screen"
      aria-hidden="true"
      className={`fixed inset-0 z-[200] bg-[#0B1F3A] flex flex-col items-center justify-center transition-opacity duration-[450ms] ${
        phase === "leaving" ? "opacity-0 pointer-events-none" : "opacity-100"
      }`}
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="relative flex flex-col items-center splash-rise">
        <img
          src="/brand-mark.png"
          alt="Café Breizh"
          className="h-28 w-28 md:h-36 md:w-36 rounded-3xl shadow-[0_18px_52px_-10px_rgba(0,0,0,0.6)] ring-1 ring-white/10"
        />
        <p className="mt-6 text-[11px] tracking-[0.4em] uppercase text-[#D9B466] splash-tagline">
          Control · Organize · Grow
        </p>
        <div className="mt-5 h-[2px] w-10 bg-white/20 rounded-full overflow-hidden">
          <div className="h-full bg-[#F07C52] splash-bar" />
        </div>
      </div>
    </div>
  );
}
